import { TourProvider } from '@reactour/tour';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { SetupTourContext, type SetupTourControls } from './SetupTourContext';
import { SetupTourController, type SetupTourControllerHandle } from './SetupTourController';
import { renderSetupTourNextButton, renderSetupTourPreviousButton } from './SetupTourNavigation';
import type { SetupTourRegistry } from './types';

type SetupTourProviderProps = {
  registry: SetupTourRegistry;
  children: ReactNode;
};

export const SETUP_TOUR_MASK_COLOR = 'var(--setup-tour-mask)';

const setupTourStyles = {
  maskWrapper: (base: Record<string, unknown>) => ({
    ...base,
    color: SETUP_TOUR_MASK_COLOR,
  }),
  // reactour's default popover hardcodes a white background but leaves `color: inherit` -
  // in dark mode that inherits the page's light body text, so the copy is unreadable.
  popover: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: 'var(--surface-card)',
    color: 'var(--app-text)',
  }),
  // The default controls row never wraps, so a step with many dots (long tours) plus a
  // secondary action (e.g. "Finish") runs out of room and the flex items get squeezed
  // below their own content width - which wraps the Back/Next button's icon and label
  // onto separate lines instead of moving whole buttons to a new row. Moving the dots
  // onto their own row first (via `order`/`flexBasis` below), with Back(+Finish) and
  // Continue sharing the row underneath, keeps every button intact and matches the
  // classic Back-left/Next-right layout instead of stacking each control on its own line.
  controls: (base: Record<string, unknown>) => ({
    ...base,
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-start' as const,
    gap: 8,
  }),
  button: (base: Record<string, unknown>, state?: { kind?: string }) => ({
    ...base,
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    gap: 4,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    order: state?.kind === 'prev' ? 2 : undefined,
  }),
  // Forced onto its own full-width, centered row, first - ahead of Back/Finish and
  // Continue on the row below - the dots themselves already wrap internally, so this
  // only affects where that block sits relative to the buttons, not how the dots lay
  // out among themselves.
  navigation: (base: Record<string, unknown>) => ({
    ...base,
    minWidth: 0,
    order: 1,
    flexBasis: '100%' as const,
    justifyContent: 'center' as const,
  }),
};

export const SetupTourProvider = ({
  registry,
  children,
}: SetupTourProviderProps): React.JSX.Element => {
  const controllerRef = useRef<SetupTourControllerHandle>(null);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const controls = useMemo<SetupTourControls>(
    () => ({
      activeTourId,
      isTransitioning,
      startTour: (tourId) => controllerRef.current?.startTour(tourId),
      next: () => controllerRef.current?.next() ?? Promise.resolve(),
      goToStep: (stepId) => controllerRef.current?.goToStep(stepId) ?? Promise.resolve(),
      previous: () => controllerRef.current?.previous() ?? Promise.resolve(),
      close: () => controllerRef.current?.close(),
    }),
    [activeTourId, isTransitioning],
  );

  return (
    <SetupTourContext.Provider value={controls}>
      <TourProvider
        steps={[]}
        disableDotsNavigation
        styles={setupTourStyles}
        nextButton={renderSetupTourNextButton}
        prevButton={renderSetupTourPreviousButton}
        onClickClose={() => controllerRef.current?.close()}
      >
        <SetupTourController
          ref={controllerRef}
          registry={registry}
          onActiveTourChange={setActiveTourId}
          onTransitionChange={setIsTransitioning}
        />
        {children}
      </TourProvider>
    </SetupTourContext.Provider>
  );
};
